import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Info, Loader2 } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { errorText } from "./cockpitErrors";
import { onboardingAdminAPI, type AdoptionState, type LeadStatus } from "../../OnboardingAPI";

export const ADOPTION_RUNNING_NOTICE = "Die Dateien werden gerade in den Mandanten übernommen. Warte, bis die Übernahme fertig ist.";
export const FILES_LOST_NOTICE = "Die hochgeladenen Dateien sind noch nicht im Mandanten und gehen beim Löschen verloren.";

/** What the dialog can know about the lead: whether the files are on their way into the tenant (no deleting then), or not there yet (they are lost). */
export function deleteGuard(lead: { adoptionState?: AdoptionState; status?: LeadStatus; fileCount?: number }): { wait: boolean; filesLost: boolean } {
  const state = lead.adoptionState;
  const wait = state === "pending" || state === "running";
  const notReleasedWithFiles = lead.status !== undefined && lead.status !== "report_ready" && (lead.fileCount ?? 0) > 0;
  return { wait, filesLost: !wait && (state === "failed" || state === "waiting_for_customer" || (state === undefined && notReleasedWithFiles)) };
}

/**
 * Deleting a lead for good (from the list and from the detail view): the lead and the files that have not moved into the
 * tenant yet. The tenant and everything adopted stays. No typing: the dialog names the company in plain text, focus starts
 * on "Abbrechen" (a stray Enter deletes nothing), and the red button is off while the call runs. A refusal (an older server
 * that still asks for a confirmation answers 400) is a line in the dialog, not an error dialog.
 */
export default function DeleteLeadDialog({
  open,
  leadId,
  companyName,
  customerId,
  adoptionState,
  status,
  fileCount,
  onClose,
  onDeleted,
  onError,
}: {
  open: boolean;
  leadId: string | null;
  companyName: string;
  /** Set when a tenant was created for the lead: the dialog says it stays. */
  customerId?: string;
  /** The lead's state of file adoption, its status and file count, when known (the list row has them as well). */
  adoptionState?: AdoptionState;
  status?: LeadStatus;
  fileCount?: number;
  onClose: () => void;
  onDeleted: () => void;
  onError?: (error: unknown) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const guard = deleteGuard({ adoptionState, status, fileCount });

  useEffect(() => {
    if (open) setError(null);
  }, [open, leadId]);

  const remove = async () => {
    if (!leadId || busy || guard.wait) return;
    setBusy(true);
    setError(null);
    try {
      await onboardingAdminAPI.remove(leadId);
      onDeleted();
    } catch (caught) {
      const known = caught as { code?: string; status?: number; detail?: string; message?: string };
      // 400: an older server that still asks for a confirmation; 409: the files are being moved (or another state that forbids it)
      if (known?.code === "invalid_request" || known?.code === "invalid_state" || known?.status === 400 || known?.status === 409) setError(errorText(caught, "Der Server hat das Löschen abgelehnt."));
      else onError?.(caught);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={(next) => !next && !busy && onClose()}>
      <AlertDialogContent
        onOpenAutoFocus={(event) => {
          // the safe button first
          event.preventDefault();
          cancelRef.current?.focus();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Lead löschen?</AlertDialogTitle>
          <AlertDialogDescription>
            <span data-testid="delete-company">„{companyName}“</span> wird gelöscht: der Lead und die noch nicht in den Mandanten übernommenen Dateien,
            endgültig. Der angelegte Mandant und alle in den Mandanten übernommenen Dateien, Gebäude und Anlagen bleiben bestehen.
            {customerId && (
              <span data-testid="delete-tenant-note"> Der Mandant {customerId} bleibt bestehen und muss bei Bedarf separat entfernt werden.</span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {guard.wait && (
          <div role="note" className="flex items-start gap-3 rounded-2xl border border-border bg-white p-3 text-sm shadow-sm dark:bg-card" data-testid="delete-adoption-wait">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <p>{ADOPTION_RUNNING_NOTICE}</p>
          </div>
        )}
        {guard.filesLost && (
          <div role="note" className="flex items-start gap-3 rounded-2xl border border-warning/50 bg-white p-3 text-sm font-medium shadow-sm dark:bg-card" data-testid="delete-files-warning">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
            <p>{FILES_LOST_NOTICE}</p>
          </div>
        )}
        <p className="sr-only" role="status" aria-live="polite">
          {busy ? "Der Lead wird gelöscht." : ""}
        </p>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel ref={cancelRef} disabled={busy}>
            Abbrechen
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={busy || guard.wait}
            aria-busy={busy}
            className="min-w-[12rem] bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={(event) => {
              // the dialog stays open while the call runs, and a refusal keeps it open
              event.preventDefault();
              void remove();
            }}
          >
            {/* the longer text stays in the layout (invisible), so the button keeps its width while the spinner shows */}
            <span className="inline-grid">
              <span className="invisible col-start-1 row-start-1" aria-hidden="true">Endgültig löschen</span>
              <span className="col-start-1 row-start-1 inline-flex items-center justify-center">
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" data-testid="delete-spinner" />}
                {busy ? "Wird gelöscht …" : "Endgültig löschen"}
              </span>
            </span>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
